import { useCallback, useEffect, useState } from "react";
import { drainQueue, onQueueChanged, queueCount } from "./offlineQueue.js";

export interface OfflineQueueStatus {
  count: number;
  /** Tries to send everything queued right now, whether or not the browser thinks it is online. */
  retry: () => void;
}

/**
 * The count `AppShell`'s badge shows, and the two moments a queue should try
 * to empty itself without anybody asking: the app opening (in case anything
 * was left over from last time) and the browser saying the connection is
 * back.
 *
 * `onStorage`, when given, hears about a drain's last upload -- a background
 * send the capture screen never saw would otherwise leave the sidebar's own
 * storage figure stale until something else happened to refresh it.
 */
export function useOfflineQueue(onStorage?: (usedBytes: number) => void): OfflineQueueStatus {
  const [count, setCount] = useState(0);

  const refresh = useCallback(() => {
    void queueCount().then(setCount);
  }, []);

  const retry = useCallback(() => {
    void drainQueue().then(({ remaining, usedBytes }) => {
      setCount(remaining);
      if (usedBytes !== undefined) onStorage?.(usedBytes);
    });
  }, [onStorage]);

  useEffect(() => {
    retry();
    const off = onQueueChanged(refresh);
    window.addEventListener("online", retry);
    return () => {
      off();
      window.removeEventListener("online", retry);
    };
  }, [refresh, retry]);

  return { count, retry };
}
